/**
 * `hotels` — where the party sleeps: one row per hotel stay.
 * Check-in/out are venue-local wall times (ARCHITECTURE.md §12).
 * Money fields are tagged `financial` (owner/editor-only, §3.2).
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

/** YYYY-MM-DD HH:MM (or with a T separator), venue-local wall time (§12). */
const LOCAL_DATETIME = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}[ T]([01]\d|2[0-3]):[0-5]\d$/, "expected YYYY-MM-DD HH:MM venue-local wall time");

export const hotels = defineCollection({
  key: "hotels", // the ONLY place this string is born (CONSTRAINTS #5)
  labelKey: "collections.hotels",
  icon: "bed",
  sheetTab: "Hotels",
  dedupFields: ["name", "check_in"], // -> op dedup_key (§4.1)
  sort: [{ field: "check_in" }],
  split: "logistics", // NOTE: canonical 4-split names are pinned by the P3 UI work
  chatHint:
    "Accommodation: one row per hotel stay — hotel name, address, check-in/check-out, booking " +
    "reference. Costs go in the cost field (owner/editor-only). Travel legs to/from the hotel " +
    "belong in the travel collection (ships in P3), not here.",
  fields: {
    name: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "hotels.name",
      sheet: { column: "Name" },
      description: "Hotel name as bookable/searchable, e.g. \"Motel One Berlin-Alexanderplatz\".",
    }),
    address: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "hotels.address",
      sheet: { column: "Address" },
      description: "Street address — what a taxi or maps app needs.",
    }),
    check_in: field({
      type: LOCAL_DATETIME.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "hotels.check_in",
      renamedFrom: ["checkin"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Check-in" },
      description: "Check-in date+time (YYYY-MM-DD HH:MM), venue-local wall time — never tz-converted.",
    }),
    check_out: field({
      type: LOCAL_DATETIME.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "hotels.check_out",
      renamedFrom: ["checkout"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Check-out" },
      description: "Check-out date+time (YYYY-MM-DD HH:MM), venue-local wall time.",
    }),
    phone: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "hotels.phone",
      input: "tel",
      sheet: { column: "Phone" },
      description: "Front-desk phone number — late arrivals, lost keys, taxi bookings.",
    }),
    booking_ref: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "hotels.booking_ref",
      renamedFrom: ["confirmation"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Booking ref" },
      description: "Reservation / confirmation number the front desk asks for.",
    }),
    breakfast_included: field({
      type: z.boolean().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "hotels.breakfast_included",
      sheet: { column: "Breakfast" },
      description: "Whether breakfast is included in the booking.",
    }),
    cost: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "financial", // owner/editor only — never leaves full scope
      labelKey: "hotels.cost",
      sheet: { column: "Cost" },
      description: "Total cost of the stay, free-form with currency (e.g. \"340 EUR\"). Never leaves owner/editor scope.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "hotels.notes",
      sheet: { column: "Notes" },
      description: "Free-form notes: parking, late check-in arrangements, wifi code.",
    }),
  },
});
